/**
 * Lot Polygon Colors DOM Component
 *
 * Sole owner of color-related class mutations on the ISM SVG overlay.
 * Applies base color classes, selected state, and hover state to lot <path> elements.
 * Does not know about Redux or business logic — receives the lot status map from the plugin.
 *
 * @module nativeInteractiveSiteMap/components/lotPolygonColors.dom
 */

import LotStatus from '../enums/lotStatus';

const LOT_CLASS_BASE = 'nhs-c-native-interactive-site-map__lot';
const LOT_SELECTED_CLASS = `${LOT_CLASS_BASE}--selected`;
const LOT_HOVERED_CLASS = `${LOT_CLASS_BASE}--hovered`;
const LOT_NON_INTERACTIVE_CLASS = `${LOT_CLASS_BASE}--non-interactive`;

/**
 * @param {SVGSVGElement} svgElement
 * @param {string} lotId
 * @returns {Element|null}
 */
function findLotElement(svgElement, lotId) {
  return svgElement.getElementById(lotId);
}

/**
 * Creates a controller for color-related CSS class mutations on lot <path> elements
 * within the given SVG element.
 *
 * @param {SVGSVGElement} svgElement - The root SVG element containing lot <path> elements
 * @returns {{ applyInitialColors: Function, setSelected: Function, setHovered: Function, clearHovered: Function }}
 */
function createLotPolygonColors(svgElement) {
  const state = {
    currentHoveredLotId: null
  };

  return {
    /**
     * Applies the status class to every lot element found in the SVG.
     * Skips lotIds not present in the SVG without throwing.
     *
     * @param {Record<string, string>} lotStatusMap - { lotId → status string }
     */
    applyInitialColors(lotStatusMap) {
      Object.entries(lotStatusMap).forEach(([lotId, status]) => {
        const element = findLotElement(svgElement, lotId);
        if (element) {
          element.classList.add(`${LOT_CLASS_BASE}--${status}`);
          if (status === `${LotStatus.Unavailable}`) {
            element.classList.add(LOT_NON_INTERACTIVE_CLASS);
          }
        }
      });
    },

    /**
     * Moves the selected class from prevLotId to newLotId.
     * Passing null for either argument is safe.
     *
     * @param {string|null} prevLotId
     * @param {string|null} newLotId
     */
    setSelected(prevLotId, newLotId) {
      if (prevLotId) {
        findLotElement(svgElement, prevLotId)?.classList.remove(LOT_SELECTED_CLASS);
      }
      if (newLotId) {
        findLotElement(svgElement, newLotId)?.classList.add(LOT_SELECTED_CLASS);
      }
    },

    /**
     * Adds the hovered class to the given lot element.
     * Automatically clears the previously hovered element first.
     *
     * @param {string} lotId
     */
    setHovered(lotId) {
      if (state.currentHoveredLotId && state.currentHoveredLotId !== lotId) {
        findLotElement(svgElement, state.currentHoveredLotId)?.classList.remove(LOT_HOVERED_CLASS);
      }
      findLotElement(svgElement, lotId)?.classList.add(LOT_HOVERED_CLASS);
      state.currentHoveredLotId = lotId;
    },

    /**
     * Removes the hovered class from whichever element is currently hovered.
     * No-op if nothing is currently hovered.
     */
    clearHovered() {
      if (state.currentHoveredLotId) {
        findLotElement(svgElement, state.currentHoveredLotId)?.classList.remove(LOT_HOVERED_CLASS);
        state.currentHoveredLotId = null;
      }
    },
  };
}

export default createLotPolygonColors;